/**
 * @description Merchant dashboard: overview metrics, take-payment, and visible-tab transaction poll.
 *
 * @dependencies
 * - AuthContext: user/company, getToken, signOut
 * - transactionsService / customersService / bankAccountsService / gatewaysService
 * - transactionPollPolicy: visibility + 401-only sign-out
 *
 * @notes
 * - Transaction poll is 30s while the tab is visible; HTTP 401 clears local tokens then signOut
 * - HTTP 403 is not treated as session expiry
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import { useUser, useAuth, useCompany } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { customersService, transactionsService, bankAccountsService, gatewaysService } from '../services/api';
import { formatCurrency } from '../utils/invoiceHelpers';
import CardEntry from './shared/CardEntry';
import SurchargeConsentModal from './shared/SurchargeConsentModal';
import PaymentErrorBanner from './shared/PaymentErrorBanner';
import { getCustomerDisplayName, getCustomerSearchString } from '../utils/customerHelpers';
import StatusBadge from './shared/StatusBadge';
import OnboardingModal from './onboarding/OnboardingModal';
import CompanyInviteModal from './company/CompanyInviteModal';
import CalendarView from './scheduling/Calendar/CalendarView';
import PermissionGate from './shared/PermissionGate';
import { PERMISSIONS } from '../constants/permissionMappings';
import usePaymentStatus from '../hooks/usePaymentStatus';
import { shouldRunTransactionPoll, isAuthFailureStatus, clearLocalAuthTokensOnPollFailure } from '../utils/transactionPollPolicy';
import apiClient from '../services/api/apiClient';

/** Product knob: dashboard transaction refresh cadence while the tab is visible. */
const TRANSACTION_POLL_INTERVAL_MS = 30000;

function MerchantDashboard({ activeTab: initialTab = 'overview' }) {
  const { user } = useUser();
  const { getToken, signOut } = useAuth();
  const { company } = useCompany();
  const navigate = useNavigate();
  const paymentConfigured = usePaymentStatus();
  const [paymentResult, setPaymentResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState('');
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [showPaymentResult, setShowPaymentResult] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [activeTab, setActiveTab] = useState(initialTab);
  const [isCreateDropdownOpen, setIsCreateDropdownOpen] = useState(false);
  const [selectedMetric, setSelectedMetric] = useState('Total Sales');
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState(
  today.toLocaleDateString('en-CA', { year: 'numeric', month: '2-digit', day: '2-digit' }).split('/').join('-')
  );
const [userTimezone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [transactions, setTransactions] = useState([]);
  const [metrics, setMetrics] = useState({
    totalSales: 0,
    customerCount: 0,
    transactionCount: 0
  });
  const createDropdownRef = useRef(null);
  const metricsDropdownRef = useRef(null);
  const modalRef = useRef(null);
  const [isMetricsDropdownOpen, setIsMetricsDropdownOpen] = useState(false);
  const [retryKey, setRetryKey] = useState(0);
  
  // New state for customer selection and billing details
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);
  const customerDropdownRef = useRef(null);

  // Onboarding modal state
  const [showOnboardingModal, setShowOnboardingModal] = useState(false);
  const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false);

  // Surcharge two-phase state (matches PaymentRecorder + portal flow)
  const [surchargeInfo, setSurchargeInfo] = useState(null);
  const [surchargeConsentPending, setSurchargeConsentPending] = useState(false);
  const [pendingPaymentData, setPendingPaymentData] = useState(null);
  const [hpfSessionKey, setHpfSessionKey] = useState(0); // Increment to force HPF remount

  // Idempotency key — persists across retries within the same payment session.
  // Generated once per payment attempt, sent to /process-payment so retries after
  // surcharge consent don't double-charge. Cleared on success or session reset.
  const idempotencyKeyRef = useRef(null);

  // Company invite modal state
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteParams, setInviteParams] = useState(null);

  // Retry function for error handling
  const retryFetch = () => {
    setRetryKey(prev => prev + 1);
  };

  // Check for pending company invite in sessionStorage (from AcceptCompanyInvite redirect)
  useEffect(() => {
    const pending = sessionStorage.getItem('pending_company_invite');
    if (pending) {
      sessionStorage.removeItem('pending_company_invite');
      try {
        const params = JSON.parse(pending);
        if (params.employeeId && params.companyId) {
          setInviteParams(params);
          setShowInviteModal(true);
        }
      } catch { /* ignore malformed JSON */ }
    }
  }, []);

  // Wrap resetFormFields in useCallback
  const resetFormFields = useCallback(() => {
    setAmount('');
    setSelectedCustomer(null);
    setCustomerSearchTerm('');
    setError(null);
  }, []);

  // Wrap closePaymentModal in useCallback
  const closePaymentModal = useCallback(() => {
    if (showPaymentResult) {
      // If showing results, reset everything
      setShowPaymentForm(false);
      setShowPaymentResult(false);
      setPaymentResult(null);
      resetFormFields();
    } else {
      // If still in form, just close the modal
      setShowPaymentForm(false);
    }
  }, [showPaymentResult, resetFormFields]);

  // Initial data fetch with proper cancellation
  // Check onboarding status when component mounts
  useEffect(() => {
    const checkOnboardingStatus = async () => {
      if (!user?.id || hasCheckedOnboarding) return;

      try {
        // Check if user has company from CompanyContext
        const companyId = company?.id;

        // If companyId is null or undefined, show onboarding modal
        if (!companyId) {
          setShowOnboardingModal(true);
        }

        setHasCheckedOnboarding(true);
      } catch (error) {
        console.error('Failed to check onboarding status:', error);
        setHasCheckedOnboarding(true);
      }
    };

    checkOnboardingStatus();
  }, [user?.id, company?.id, hasCheckedOnboarding]);

  useEffect(() => {
    if (!user?.id) return;

    const abortController = new AbortController();

    const fetchData = async () => {
      try {
        await getToken(); // Ensure token is available

        // Use allSettled so a single permission-denied (e.g. bank_accounts.view
        // missing on a custom role) doesn't blow up the whole dashboard. Bank
        // accounts are optional context here — empty array is a valid state.
        const results = await Promise.allSettled([
          transactionsService.getMerchantTransactions(),
          customersService.getMerchantCustomers(),
          bankAccountsService.getMerchantBankAccounts(),
        ]);

        if (abortController.signal.aborted) return;

        const [transactionsResult, customersResult, bankAccountsResult] = results;

        if (transactionsResult.status === 'fulfilled') {
          setTransactions(transactionsResult.value);
        } else {
          console.error('Failed to fetch transactions:', transactionsResult.reason);
        }

        if (customersResult.status === 'fulfilled') {
          // Handle structured response { customers: [], pagination: {} }
          const customersData = customersResult.value.customers || customersResult.value || [];
          setCustomers(customersData);
        } else {
          console.error('Failed to fetch customers:', customersResult.reason);
        }

        if (bankAccountsResult.status === 'fulfilled') {
          setBankAccounts(bankAccountsResult.value);
        } else {
          // Likely permission denied (bank_accounts.view) — degrade silently.
          console.warn('Bank accounts unavailable:', bankAccountsResult.reason?.message);
          setBankAccounts([]);
        }

        // Only surface a global error if ALL three failed (real connectivity issue,
        // not a single permission gap).
        if (results.every(r => r.status === 'rejected')) {
          setError('Failed to fetch dashboard data. Please refresh or contact support.');
        }
      } catch (err) {
        if (!abortController.signal.aborted) {
          console.error('Unexpected dashboard fetch error:', err);
          setError('Failed to fetch data: ' + err.message);
        }
      }
    };

    fetchData();

    // Cleanup function - cancels ongoing requests
    return () => {
      abortController.abort();
    };
  }, [user?.id, retryKey, getToken]);

  // Add the fixed useEffect hook to handle transaction created events
  useEffect(() => {
    // Function to handle transaction created events
    const handleTransactionCreated = () => {
      // Clear cached transactions to force a fresh fetch
      localStorage.removeItem('cachedTransactions');
      localStorage.removeItem('cachedTransactionsTimestamp');
      // Trigger data refresh
      retryFetch();
    };
    
    // Add event listener
    window.addEventListener('transaction-created', handleTransactionCreated);
    
    // Clean up
    return () => {
      window.removeEventListener('transaction-created', handleTransactionCreated);
    };
  }, []);

  // Single visible-tab poll for live transaction updates (no App.js global poll).
  useEffect(() => {
    if (!user?.id) return;

    let cancelled = false;
    let pollInterval = null;
    let inFlight = false;
    let signedOut = false;

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const pollOnce = async () => {
      if (
        cancelled ||
        signedOut ||
        inFlight ||
        !shouldRunTransactionPoll({
          visibilityState: document.visibilityState,
          signedOut
        })
      ) {
        return;
      }

      inFlight = true;
      try {
        const freshTransactions = await transactionsService.getMerchantTransactions();
        if (cancelled) return;

        const next = Array.isArray(freshTransactions)
          ? freshTransactions
          : (freshTransactions?.data || []);

        setTransactions((prevTransactions) => {
          if (JSON.stringify(next) !== JSON.stringify(prevTransactions)) {
            return next;
          }
          return prevTransactions;
        });
      } catch (err) {
        const status = err?.response?.status;
        if (isAuthFailureStatus(status)) {
          signedOut = true;
          stopPolling();
          console.warn('Transaction poll stopped — session unauthorized', { status });
          // Drop local tokens before signOut's /auth/logout round-trip so a
          // dead session (e.g. logout-all in another tab) does not flash signed-in.
          clearLocalAuthTokensOnPollFailure({ sessionStorage, apiClient });
          if (typeof signOut === 'function') {
            try {
              await signOut();
            } catch (signOutErr) {
              console.error('Sign-out after poll auth failure failed:', signOutErr);
            }
          }
          return;
        }
        console.error('Failed to refresh transactions:', err);
      } finally {
        inFlight = false;
      }
    };

    const startPolling = () => {
      if (cancelled || signedOut || pollInterval) return;
      pollInterval = setInterval(pollOnce, TRANSACTION_POLL_INTERVAL_MS);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        startPolling();
      } else {
        stopPolling();
      }
    };

    if (document.visibilityState === 'visible') {
      startPolling();
    }
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      stopPolling();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user?.id, signOut]);

  useEffect(() => {
    if (!transactions.length && !customers.length) return;

    // Filter transactions by comparing in the user's local timezone
    const filteredTransactions = transactions.filter(txn => {
      const txnDate = new Date(txn.date || txn.created_at);
      
      // Convert to local date string for comparison
      const txnLocalDate = txnDate.toLocaleDateString('en-CA');
      
      return txnLocalDate === selectedDate && txn.status === 'APPROVAL';
    });
  
    const filteredCustomers = customers.filter(customer => {
      const customerDate = new Date(customer.createdAt);
      const customerLocalDate = customerDate.toLocaleDateString('en-CA');
      return customerLocalDate === selectedDate;
    });
  
    const dailyTotal = filteredTransactions.reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
    setMetrics({
      totalSales: dailyTotal,
      customerCount: filteredCustomers.length,
      transactionCount: filteredTransactions.length
    });
  }, [selectedDate, transactions, customers, userTimezone]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (createDropdownRef.current && !createDropdownRef.current.contains(event.target)) {
        setIsCreateDropdownOpen(false);
      }
      if (metricsDropdownRef.current && !metricsDropdownRef.current.contains(event.target)) {
        setIsMetricsDropdownOpen(false);
      }
      if (customerDropdownRef.current && !customerDropdownRef.current.contains(event.target)) {
        setIsCustomerDropdownOpen(false);
      }
      if (modalRef.current && !modalRef.current.contains(event.target) && showPaymentForm) {
        closePaymentModal();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPaymentForm, closePaymentModal]);


  const handleTabChange = (tab) => {
    setActiveTab(tab);
    navigate(`/merchant/dashboard?tab=${tab}`);
  };

  /**
   * Post-charge UI updates: payment result card, transactions refresh, event dispatch.
   * Called by both the no-surcharge HPF path and the post-consent charge path.
   */
  const finalizePaymentSuccess = (response, formData) => {
    setPaymentResult({
      status: response.success ? 'APPROVAL' : 'DECLINE',
      message: response.success ? 'Transaction approved' : 'Transaction declined',
      transactionId: response.transactionId || response.gatewayTransactionId,
      transactionReference: response.transactionId || response.gatewayTransactionId,
      amount: parseFloat(amount),
      cardNumber: formData.last4 || '****',
      brand: formData.brand,
      date: new Date().toISOString()
    });
    setShowPaymentResult(true);

    try {
      if (transactionsService.cache) {
        transactionsService.cache.merchantTransactions = {};
      }

      if (user?.id) {
        setTimeout(async () => {
          try {
            const freshTransactions = await transactionsService.getMerchantTransactions();
            if (freshTransactions?.data) {
              setTransactions(freshTransactions.data);

              const [year, month, day] = selectedDate.split('-').map(Number);
              const filteredTransactions = freshTransactions.data.filter(txn => {
                const txnDate = new Date(txn.date || txn.created_at);
                return txnDate.getFullYear() === year &&
                  txnDate.getMonth() === month - 1 &&
                  txnDate.getDate() === day &&
                  txn.status === 'APPROVAL';
              });

              const dailyTotal = filteredTransactions.reduce((sum, txn) => sum + parseFloat(txn.amount), 0);
              setMetrics(prevMetrics => ({
                ...prevMetrics,
                totalSales: dailyTotal,
                transactionCount: filteredTransactions.length
              }));
            }
          } catch (refreshErr) {
            console.error('Failed to refresh transactions:', refreshErr);
          }
        }, 1500);
      }

      try {
        const event = new CustomEvent('transaction-created', {
          detail: {
            id: response.transactionId || response.transaction_reference,
            timestamp: new Date().toISOString()
          }
        });
        window.dispatchEvent(event);
      } catch (eventError) {
        console.error('Error dispatching event:', eventError);
      }

    } catch (refreshErr) {
      console.error('Error in refresh logic:', refreshErr);
    }
  };

  /**
   * Execute the standalone charge against /gateways/process-payment using a
   * vaulted storedCard (Phase 2 of the surcharge flow). Reused by both the
   * no-surcharge and post-consent paths so retry behavior + idempotency are
   * identical.
   */
  const executeStandaloneCharge = async (formData, storedCard, surchargeAdviceId) => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = crypto.randomUUID();
    }
    const idempotencyKey = idempotencyKeyRef.current;
    const amountInCents = Math.round(parseFloat(amount) * 100);

    const response = await gatewaysService.processPayment({
      amount: amountInCents,
      currency: 'USD',
      storedCardId: storedCard.id, // local UUID (resolved server-side via PaymentVaultService)
      customerId: selectedCustomer.id,
      description: `Payment from ${getCustomerDisplayNameForSelection(selectedCustomer)}`,
      surchargeAdviceId: surchargeAdviceId || undefined,
      shopperId: storedCard.gatewayShopperId || undefined,
      cardholderName: formData.holderName
      // Billing fields intentionally omitted — already on the StoredCard from vault time
    }, { idempotencyKey });

    finalizePaymentSuccess(response, formData);

    // Payment succeeded — clear key for next session
    idempotencyKeyRef.current = null;
  };

  /**
   * Handle card submission from CardEntry (HPF or legacy).
   * HPF path uses two-phase flow (vault → check surcharge → consent → charge)
   * matching PaymentRecorder + portal patterns so surcharge is correctly applied.
   */
  const handlePaymentSubmit = async (formData) => {
    // Validate customer selection and amount
    if (!selectedCustomer) {
      setError('Please select a customer.');
      return;
    }
    if (!amount || parseFloat(amount) <= 0) {
      setError('Please enter a valid amount.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      if (formData.type === 'hostedCard') {
        // ── HPF Path: Two-phase surcharge flow ──
        // Phase 1: Vault hosted card → StoredCard
        const vaultResult = await gatewaysService.vaultCard(formData.sessionId, {
          hostedCardId: formData.hostedCardId,
          customerId: selectedCustomer.id,
          customerData: { fullName: formData.holderName }
        });
        const storedCard = vaultResult.storedCard;

        // Phase 2: Check surcharge eligibility
        const amountInCents = Math.round(parseFloat(amount) * 100);
        let surcharge = null;
        try {
          const surchargeResult = await gatewaysService.checkSurcharge({
            storedCardId: storedCard.gatewayCardId,
            amountCents: amountInCents
          });
          surcharge = surchargeResult.surcharge;
        } catch (surchargeErr) {
          // Non-fatal: proceed without surcharge (e.g. debit cards Elavon rejects)
          console.error('Surcharge check failed:', surchargeErr);
        }

        if (surcharge) {
          // Surcharge applies — pause and show consent modal
          setSurchargeInfo(surcharge);
          setPendingPaymentData({ formData, storedCard });
          setSurchargeConsentPending(true);
          setLoading(false);
          return; // Wait for user consent
        }

        // No surcharge — charge immediately
        await executeStandaloneCharge(formData, storedCard, null);
      } else {
        // ── Legacy Path: Mock gateway (no HPF, no surcharge) ──
        const payloadData = {
          userId: user.id,
          amount: amount || '10.00',
          brand: formData.brand || 'card',
          last4: formData.last4 || '****',
          avs_response: formData.billingAddress && formData.billingZip ? 'Y' : 'N',
          cvv_response: 'M',
          date: selectedDate + 'T12:00:00.000Z'
        };

        if (selectedCustomer?.id) {
          payloadData.customerId = selectedCustomer.id;
        }

        const response = await transactionsService.createMockTransaction(payloadData);

        setPaymentResult({
          ...response,
          transactionId: response.transactionId || response.transaction_reference,
          transactionReference: response.transaction_reference,
          cardNumber: response.last4 || formData.last4 || '****',
          status: 'APPROVAL',
          message: 'Transaction approved',
          amount: parseFloat(amount),
          date: new Date().toISOString()
        });
        setShowPaymentResult(true);
        finalizePaymentSuccess(response, formData);
      }
    } catch (err) {
      console.error('Payment failed:', err);
      setError(err);

      const responseData = err.response?.data || {};
      setPaymentResult({
        status: 'DECLINE',
        message: responseData.error || err.message || 'Transaction declined',
        code: responseData.code || null,
        canRetry: responseData.canRetry || false,
        isUserRecoverable: responseData.isUserRecoverable || false
      });
      setShowPaymentResult(true);
    } finally {
      setLoading(false);
    }
  };

  /** Accept surcharge — resume payment with surchargeAdviceId */
  const handleSurchargeAccept = async () => {
    setSurchargeConsentPending(false);
    setLoading(true);
    try {
      await executeStandaloneCharge(
        pendingPaymentData.formData,
        pendingPaymentData.storedCard,
        surchargeInfo.adviceId
      );
    } catch (err) {
      console.error('Payment failed after surcharge consent:', err);
      setError(err);
      const responseData = err.response?.data || {};
      setPaymentResult({
        status: 'DECLINE',
        message: responseData.error || err.message || 'Transaction declined'
      });
      setShowPaymentResult(true);
    } finally {
      setLoading(false);
      setSurchargeInfo(null);
      setPendingPaymentData(null);
    }
  };

  /** Decline surcharge — reset HPF session so merchant can retry with fresh fields */
  const handleSurchargeDecline = () => {
    setSurchargeConsentPending(false);
    setSurchargeInfo(null);
    setPendingPaymentData(null);
    idempotencyKeyRef.current = null;
    setHpfSessionKey(prev => prev + 1); // Force CardEntry/HPF remount with fresh session
  };

  const handleCreateDropdownToggle = () => {
    setIsCreateDropdownOpen(!isCreateDropdownOpen);
  };
  
  // Navigate to products page for product creation
  const navigateToProducts = () => {
    setIsCreateDropdownOpen(false);
    navigate('/merchant/products?openModal=product');
  };

  // Navigate to customers page for customer creation
  const navigateToCustomers = () => {
    setIsCreateDropdownOpen(false);
    navigate('/merchant/customers?openModal=customer');
  };

  const handleMetricsDropdownToggle = () => {
    setIsMetricsDropdownOpen(!isMetricsDropdownOpen);
  };

  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setIsCustomerDropdownOpen(false);
    setCustomerSearchTerm('');
  };
  
  // Display name helper function
  // Use centralized customer display logic
  const getCustomerDisplayNameForSelection = (customer) => {
    const displayName = getCustomerDisplayName(customer);
    return displayName !== 'Guest' ? displayName : '';
  };

  const startNewTransaction = useCallback(() => {
    resetFormFields();
    setShowPaymentResult(false);
    setPaymentResult(null);
  }, [resetFormFields]);

  return (
    <div className="min-h-screen">
      {/* Onboarding Modal */}
      <OnboardingModal
        isOpen={showOnboardingModal}
        onClose={() => {
          setShowOnboardingModal(false);
          // Refresh data after company creation
          retryFetch();
        }}
      />

      {/* Company Invite Modal (triggered by AcceptCompanyInvite redirect) */}
      <CompanyInviteModal
        isOpen={showInviteModal}
        onClose={(result) => {
          setShowInviteModal(false);
          if (result?.accepted) retryFetch();
        }}
        inviteParams={inviteParams}
      />

      <div className="max-w-[2000px] w-full mx-auto px-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Dashboard</h1>
          <div className="relative" ref={createDropdownRef}>
            <button
              onClick={handleCreateDropdownToggle}
              className="bg-brand text-white px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-brand-hover transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
              </svg>
              Create
            </button>
            {isCreateDropdownOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                {/* HIDDEN: standalone payments aren't appearing in transaction lists across
                    the platform — hide the entry point until that's investigated. The full
                    flow (surcharge consent, vault, charge) is still wired in this component
                    for when we re-enable. */}
                {false && paymentConfigured !== false && (
                  <PermissionGate permission={PERMISSIONS.payments.create}>
                    <button
                      onClick={() => {
                        setShowPaymentForm(true);
                        setIsCreateDropdownOpen(false);
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      New Payment
                    </button>
                  </PermissionGate>
                )}
                <PermissionGate permission={PERMISSIONS.products.create}>
                  <button
                    onClick={navigateToProducts}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    New Product
                  </button>
                </PermissionGate>
                <PermissionGate permission={PERMISSIONS.customers.create}>
                  <button
                    onClick={navigateToCustomers}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    New Customer
                  </button>
                </PermissionGate>
              </div>
            )}
          </div>
        </div>

        {showPaymentForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div ref={modalRef} className="bg-white rounded-lg shadow-lg w-full max-w-md relative max-h-[90vh] overflow-y-auto">
              {/* Close button */}
              <button 
                onClick={closePaymentModal}
                className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                aria-label="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              {showPaymentResult ? (
                // Transaction Result Screen
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4">Transaction {
                    (() => {
                      switch(paymentResult.status) {
                        case 'APPROVAL': return 'Approved';
                        case 'refunded': return 'Refunded';
                        case 'partially_refunded': return 'Partially Refunded';
                        case 'voided': return 'Voided';
                        default: return 'Declined';
                      }
                    })()
                  }</h2>
                  
                  <div className={`p-4 mb-6 rounded-lg ${
                    paymentResult.status === 'APPROVAL' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    <div className="flex items-center mb-2">
                      {paymentResult.status === 'APPROVAL' ? (
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                        </svg>
                      ) : (
                        <svg className="w-6 h-6 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      <span className="font-semibold">{paymentResult.message}</span>
                    </div>
                    {paymentResult.status !== 'APPROVAL' && paymentResult.reason && (
                      <p className="text-sm ml-8">{paymentResult.reason}</p>
                    )}
                  </div>
                  
                  <div className="space-y-3">
                    {paymentResult.status === 'APPROVAL' && (
                      <>
                        <div>
                          <p className="text-sm text-gray-500">Transaction ID</p>
                          <p className="font-medium">{paymentResult.transactionReference || paymentResult.id || 'N/A'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Amount</p>
                          <p className="font-medium">{formatCurrency(paymentResult.amount)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Card Ending</p>
                          <p className="font-medium">•••• {paymentResult.cardNumber}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Date</p>
                          <p className="font-medium">{new Date(paymentResult.date).toLocaleString()}</p>
                        </div>
                        {selectedCustomer && (
                          <div>
                            <p className="text-sm text-gray-500">Customer</p>
                            <p className="font-medium">{getCustomerDisplayNameForSelection(selectedCustomer)}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-gray-500">AVS Response</p>
                          <div className="flex items-center space-x-2">
                            <StatusBadge 
                              type="avs_code" 
                              value={paymentResult.avs_response || 'N/A'} 
                              size="xs" 
                            />
                            <p className="font-medium">{paymentResult.avsResponseText || 'Not Available'}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">CVV Response</p>
                          <div className="flex items-center space-x-2">
                            <StatusBadge 
                              type="cvv_code" 
                              value={paymentResult.cvv_response || 'N/A'} 
                              size="xs" 
                            />
                            <p className="font-medium">{paymentResult.cvvResponseText || 'Not Available'}</p>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  
                  <div className="mt-6 flex gap-3">
                    <button 
                      onClick={startNewTransaction}
                      className="flex-1 bg-brand text-white px-4 py-2 rounded-lg font-semibold hover:bg-brand-hover transition-colors"
                    >
                      New Transaction
                    </button>
                    <button
                      onClick={closePaymentModal}
                      className="flex-1 bg-gray-300 text-gray-800 px-4 py-2 rounded-lg font-semibold hover:bg-gray-400 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : (
                // Payment Form
                <div className="p-6">
                  <h2 className="text-xl font-bold mb-4 pr-6">New Payment</h2>
                  <PaymentErrorBanner error={error} onDismiss={() => setError(null)} className="mb-4" />
                  <div>
                    {/* Form sections with headers */}
                    <div className="space-y-6">
                      {/* 1. Customer Selection Section */}
                      <div>
                        <div className="flex justify-between items-center mb-2 pb-1 border-b border-gray-200">
                          <h3 className="text-md font-semibold">Select Customer <span className="text-red-500">*</span></h3>
                          {selectedCustomer && (
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCustomer(null);
                                setCustomerSearchTerm('');
                                setIsCustomerDropdownOpen(true);
                                setTimeout(() => {
                                  const inputElement = document.getElementById('customer-search-input');
                                  if (inputElement) inputElement.focus();
                                }, 50);
                              }}
                              className="text-blue-500 text-sm hover:underline"
                            >
                              Edit
                            </button>
                          )}
                        </div>
                        
                        {!selectedCustomer ? (
                          <div className="relative" ref={customerDropdownRef}>
                            <div 
                              onClick={() => setIsCustomerDropdownOpen(!isCustomerDropdownOpen)}
                              className="w-full p-2 border rounded-lg flex justify-between items-center cursor-pointer"
                            >
                              <input
                                id="customer-search-input"
                                type="text"
                                placeholder="Search customers..."
                                value={customerSearchTerm}
                                onChange={(e) => {
                                  setCustomerSearchTerm(e.target.value);
                                  setIsCustomerDropdownOpen(true);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsCustomerDropdownOpen(true);
                                }}
                                className="border-none outline-none w-full"
                                required
                              />
                              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                              </svg>
                            </div>
                            
                            {isCustomerDropdownOpen && (
                              <div className="absolute z-20 mt-1 w-full bg-white border rounded-lg shadow-lg">
                                <div className="max-h-52 overflow-y-auto">
                                  {(() => {
                                    // Get customers sorted alphabetically
                                    const sortedCustomers = [...customers].sort((a, b) => {
                                      const nameA = getCustomerDisplayNameForSelection(a).toLowerCase();
                                      const nameB = getCustomerDisplayNameForSelection(b).toLowerCase();
                                      return nameA.localeCompare(nameB);
                                    });
                                    
                                    // Filter by search term if entered
                                    const displayCustomers = customerSearchTerm
                                      ? sortedCustomers.filter(customer => {
                                          const searchLower = customerSearchTerm.toLowerCase();
                                          return getCustomerSearchString(customer).includes(searchLower);
                                        })
                                      : sortedCustomers;
                                    
                                    if (displayCustomers.length > 0) {
                                      return displayCustomers.slice(0, 7).map((customer) => (
                                        <div
                                          key={customer.id}
                                          className="p-2 hover:bg-gray-100 cursor-pointer"
                                          onClick={() => {
                                            handleCustomerSelect(customer);
                                            setIsCustomerDropdownOpen(false);
                                          }}
                                        >
                                          <div className="font-medium">{getCustomerDisplayNameForSelection(customer)}</div>
                                          {customer.email && (
                                            <div className="text-sm text-gray-500">{customer.email}</div>
                                          )}
                                        </div>
                                      ));
                                    } else {
                                      return <div className="p-2 text-gray-500">No customers found</div>;
                                    }
                                  })()}
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          // Customer details display - shown when a customer is selected
                          <div className="p-3 bg-gray-50 rounded-lg">
                            {/* Company Name [Bold] - Customer Name [Regular] */}
                            <div className="font-medium">
                              {selectedCustomer.company && (
                                <span className="font-bold">{selectedCustomer.company}</span>
                              )}
                              
                              {selectedCustomer.company && (selectedCustomer.firstName || selectedCustomer.lastName) && (
                                <span> - </span>
                              )}
                              
                              {(selectedCustomer.firstName || selectedCustomer.lastName) && (
                                <span className="font-normal">
                                  {`${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()}
                                </span>
                              )}
                            </div>
                            
                            {/* Customer Email */}
                            {selectedCustomer.email && (
                              <div className="text-sm text-gray-600 mt-1">{selectedCustomer.email}</div>
                            )}
                          </div>
                        )}
                      </div>
                                      
                      {/* 2. Amount Section */}
                      <div>
                        <h3 className="text-md font-semibold mb-2 pb-1 border-b border-gray-200">Payment Amount <span className="text-red-500">*</span></h3>
                        <div>
                          <label className="block text-gray-700 mb-1 text-sm">Amount ($)</label>
                          <input
                            type="number"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            className="w-full p-2 border rounded-lg"
                            placeholder="10.00"
                            step="0.01"
                            min="0.01"
                            required
                          />
                        </div>
                      </div>

                      {/* 3. Card Entry Section (HPF or legacy — includes billing fields + submit) */}
                      {amount && parseFloat(amount) > 0 && (
                        <div>
                          <h3 className="text-md font-semibold mb-2 pb-1 border-b border-gray-200">Payment Details</h3>
                          <CardEntry
                            key={hpfSessionKey}
                            onSubmit={handlePaymentSubmit}
                            processing={loading}
                            error={null}
                            submitButtonText={`Pay ${amount ? formatCurrency(parseFloat(amount)) : '$0.00'}`}
                            amount={Math.round((parseFloat(amount) || 0) * 100)}
                            currency="USD"
                            description={selectedCustomer ? `Payment from ${getCustomerDisplayNameForSelection(selectedCustomer)}` : 'New payment'}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {paymentResult && !showPaymentForm && paymentResult.status === 'APPROVAL' && (
          <div className="mb-6 p-4 bg-green-100 text-green-800 rounded-lg">
            <h3 className="text-lg font-semibold">Payment Successful</h3>
            <p>Transaction ID: {paymentResult.transactionReference || paymentResult.transactionId || 'N/A'}</p>
            <p>Amount: {formatCurrency(paymentResult.amount)}</p>
            {paymentResult.cardNumber && <p>Card Ending: •••• {paymentResult.cardNumber}</p>}
          </div>
        )}

        {/* Tabs - styled to match Transactions page */}
        <div className="border-b mb-6">
          <nav className="flex space-x-8">
            {['overview', 'calendar', 'balances'].map((tab) => (
              <button
                key={tab}
                onClick={() => handleTabChange(tab)}
                className={`py-4 px-1 border-b-2 font-medium text-sm transition-colors capitalize ${
                  activeTab === tab
                    ? 'border-brand text-brand'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab}
              </button>
            ))}
          </nav>
        </div>

        {activeTab === 'calendar' && (
          <CalendarView />
        )}

        {activeTab === 'overview' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  max={today.toLocaleDateString('en-CA')}
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand [&::-webkit-calendar-picker-indicator]:hover:cursor-pointer"
                  style={{
                    '--tw-ring-color': 'rgb(250 204 21 / 0.2)',
                    '--tw-ring-offset-width': '2px',
                  }}
                />
                <div className="relative" ref={metricsDropdownRef}>
                  <button
                    onClick={handleMetricsDropdownToggle}
                    className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg font-semibold flex items-center gap-2 hover:bg-gray-300 transition-colors"
                  >
                    {selectedMetric}
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  {isMetricsDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-10">
                      {['Total Sales', 'Transaction Count'].map((metric) => (
                        <button
                          key={metric}
                          onClick={() => {
                            setSelectedMetric(metric);
                            setIsMetricsDropdownOpen(false);
                          }}
                          className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                        >
                          {metric}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold">Total Sales</h3>
                <p className="text-2xl font-bold">{formatCurrency(metrics.totalSales)}</p>
              </div>
              <div className="bg-white p-4 rounded-lg shadow">
                <h3 className="text-lg font-semibold">Transaction Count</h3>
                <p className="text-2xl font-bold">{metrics.transactionCount}</p>
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <h3 className="text-lg font-semibold mb-4">Recent Transactions</h3>
              {transactions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full">
                  <thead>
                      <tr>
                        <th className="px-4 py-2 text-left">Transaction ID</th>
                        <th className="px-4 py-2 text-left">Date</th>
                        <th className="px-4 py-2 text-left">Amount</th>
                        <th className="px-4 py-2 text-left">Entry Mode</th>
                        <th className="px-4 py-2 text-left">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.slice(0, 5).map((txn, index) => (
                        <tr key={txn.id || index}>
                          <td className="px-4 py-2">
                            {(() => {
                              // Get transaction ID using fallbacks - prioritize transaction_reference
                              const txId = txn.transactionReference || 
                                          txn.id;
                              return txId || 'N/A';
                            })()}
                          </td>
                          <td className="px-4 py-2">
                           {(() => {
                              // Get date using fallbacks
                              const txDate = txn.date || txn.created_at || new Date().toISOString();
                              return new Date(txDate).toLocaleString(undefined, {
                                timeZone: userTimezone,
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit'
                              });
                            })()}
                          </td>
                          <td className="px-4 py-2">
                            <div className="flex items-center gap-1">
                              <span className={txn.transaction_type === 'refund' ? 'text-orange-600' : 'text-gray-900'}>
                                {formatCurrency(txn.amount)}
                              </span>
                              {txn.refunded_amount > 0 && (
                                <span className="text-xs text-orange-600" title={`Refunded: ${formatCurrency(txn.refunded_amount)}`}>
                                  ⚠️
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-2">
                            <StatusBadge type="entry_mode" value={txn.entryMode} size="xs" />
                          </td>
                          <td className="px-4 py-2">
                            <StatusBadge 
                              type="transaction_status" 
                              value={{
                                status: txn.status,
                                transactionType: txn.transaction_type || txn.transactionType,
                                isVoided: txn.isVoided
                              }}
                              size="xs"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p>No transactions found.</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'balances' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Balances</h2>
            {bankAccounts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {bankAccounts.map((account) => (
                  <div key={account.id} className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-lg font-semibold">{account.bankName}</h3>
                    <p>Account Ending: {account.accountNumber.slice(-4)}</p>
                    <p>Balance: {formatCurrency(account.balance)}</p>
                  </div>
                ))}
              </div>
            ) : (
              <p>No bank accounts linked.</p>
            )}
          </div>
        )}

        {/* Products tab removed - now implemented as a separate route */}
      </div>
      <SurchargeConsentModal
        show={surchargeConsentPending}
        surchargeInfo={surchargeInfo}
        onAccept={handleSurchargeAccept}
        onDecline={handleSurchargeDecline}
        formatCurrency={formatCurrency}
      />
    </div>
  );
}

export default MerchantDashboard;